/**
 * Coverage statistics calculation utilities for gaze heatmaps
 *
 * Provides metrics to analyze gaze distribution including:
 * - Coverage percentage (% of scene viewed)
 * - Dead zones (% never viewed)
 * - Entropy (exploration vs focused gaze)
 * - Gini coefficient (attention concentration)
 * - Hot spot detection (areas of highest attention)
 */

import type {
  CoverageStatistics,
  CoverageStatisticsConfig,
  HotSpot,
} from "../types/coverageStatistics";

/**
 * Calculate coverage statistics from a density grid
 *
 * @param density - Float32Array of density values (width * height)
 * @param width - Grid width
 * @param height - Grid height
 * @param config - Configuration options
 * @returns Complete coverage statistics
 */
export function calculateCoverageStatistics(
  density: Float32Array | number[],
  width: number,
  height: number,
  config: Partial<CoverageStatisticsConfig> = {},
): CoverageStatistics {
  const startTime = performance.now();

  const fullConfig: CoverageStatisticsConfig = {
    coverageThreshold: config.coverageThreshold ?? 0.01,
    maxHotSpots: config.maxHotSpots ?? 5,
    hotSpotMinDistance: config.hotSpotMinDistance ?? 0.1,
    hotSpotMinDensity: config.hotSpotMinDensity ?? 0.3,
    fovDegrees: config.fovDegrees ?? 20,
  };

  const totalCells = width * height;

  // OPTIMIZED: Single pass to find max, sum, and collect gaze points
  let maxDensity = 0;
  let sumDensity = 0;
  const gazePoints: { x: number; y: number; density: number }[] = [];
  const threshold = fullConfig.coverageThreshold;

  for (let y = 0; y < height; y++) {
    const rowOffset = y * width;
    for (let x = 0; x < width; x++) {
      const d = density[rowOffset + x];
      if (d > maxDensity) maxDensity = d;
      sumDensity += d;
    }
  }

  const meanDensity = sumDensity / totalCells;
  const normalizedThreshold = maxDensity > 0 ? threshold * maxDensity : 0;

  // Normalize density AND collect gaze points in single pass
  const normalizedDensity = new Float32Array(totalCells);
  if (maxDensity > 0) {
    const invMaxDensity = 1 / maxDensity;
    for (let y = 0; y < height; y++) {
      const rowOffset = y * width;
      for (let x = 0; x < width; x++) {
        const idx = rowOffset + x;
        const d = density[idx];
        normalizedDensity[idx] = d * invMaxDensity;
        // Collect gaze points while we're iterating
        if (d >= normalizedThreshold) {
          gazePoints.push({ x, y, density: d * invMaxDensity });
        }
      }
    }
  }

  // Sample gaze points if too many (for performance)
  // OPTIMIZED: Reuse sampled points for both exploration and coverage
  const maxGazePoints = 500;
  const sampledPoints =
    gazePoints.length > maxGazePoints
      ? gazePoints.filter((_, i) => i % Math.ceil(gazePoints.length / maxGazePoints) === 0)
      : gazePoints;

  // Calculate exploration percentage using camera FOV (50°)
  const explorationFovDegrees = 50;
  const explorationRadiusX = Math.ceil((explorationFovDegrees / 360) * width);
  const explorationRadiusY = Math.ceil((explorationFovDegrees / 180) * height);

  // Calculate coverage with foveal FOV (configurable, default 20°)
  const fovRadiusX = Math.ceil((fullConfig.fovDegrees / 360) * width);
  const fovRadiusY = Math.ceil((fullConfig.fovDegrees / 180) * height);

  // OPTIMIZED: Use single mask with bit flags (1 = covered, 2 = explored)
  const mask = new Uint8Array(totalCells);
  let exploredCells = 0;
  let coveredCells = 0;

  if (maxDensity > 0 && sampledPoints.length > 0) {
    // Mark FOV areas around each sampled gaze point
    for (const point of sampledPoints) {
      // Mark exploration area (larger FOV)
      for (let dy = -explorationRadiusY; dy <= explorationRadiusY; dy++) {
        const ny = point.y + dy;
        if (ny < 0 || ny >= height) continue;

        const rowOffset = ny * width;
        for (let dx = -explorationRadiusX; dx <= explorationRadiusX; dx++) {
          let nx = point.x + dx;
          if (nx < 0) nx += width;
          else if (nx >= width) nx -= width;

          const distX = dx / explorationRadiusX;
          const distY = dy / explorationRadiusY;
          if (distX * distX + distY * distY <= 1) {
            mask[rowOffset + nx] |= 2; // Explored flag
          }
        }
      }

      // Mark coverage area (smaller foveal FOV)
      if (fullConfig.fovDegrees > 0) {
        for (let dy = -fovRadiusY; dy <= fovRadiusY; dy++) {
          const ny = point.y + dy;
          if (ny < 0 || ny >= height) continue;

          const rowOffset = ny * width;
          for (let dx = -fovRadiusX; dx <= fovRadiusX; dx++) {
            let nx = point.x + dx;
            if (nx < 0) nx += width;
            else if (nx >= width) nx -= width;

            const distX = dx / fovRadiusX;
            const distY = dy / fovRadiusY;
            if (distX * distX + distY * distY <= 1) {
              mask[rowOffset + nx] |= 1; // Covered flag
            }
          }
        }
      }
    }

    // Count cells in single pass
    for (let i = 0; i < totalCells; i++) {
      const m = mask[i];
      if (m & 2) exploredCells++;
      if (m & 1) coveredCells++;
    }
  } else if (maxDensity > 0 && fullConfig.fovDegrees === 0) {
    // Fallback: count cells above threshold directly (already counted in gazePoints)
    coveredCells = gazePoints.length;
  }

  const explorationPercentage = (exploredCells / totalCells) * 100;
  const coveragePercentage = (coveredCells / totalCells) * 100;
  // Dead zones = areas never explored (inverse of exploration, not coverage)
  const deadZonePercentage = 100 - explorationPercentage;

  // Calculate entropy (Shannon entropy normalized)
  const entropy = calculateEntropy(normalizedDensity, totalCells);

  // Calculate Gini coefficient
  const giniCoefficient = calculateGini(normalizedDensity, totalCells);

  // Detect hot spots
  const hotSpots = detectHotSpots(
    normalizedDensity,
    width,
    height,
    fullConfig.maxHotSpots,
    fullConfig.hotSpotMinDistance,
    fullConfig.hotSpotMinDensity,
    sumDensity,
  );

  const processingTime = performance.now() - startTime;

  return {
    coveragePercentage,
    deadZonePercentage,
    explorationPercentage,
    entropy,
    giniCoefficient,
    hotSpots,
    totalCells,
    coveredCells,
    maxDensity,
    meanDensity,
    processingTime,
  };
}

/**
 * Calculate Shannon entropy normalized to [0, 1]
 *
 * Entropy measures the dispersion/uniformity of attention:
 * - 0 = All attention concentrated in one cell (perfectly focused)
 * - 1 = Attention uniformly distributed (perfectly exploratory)
 *
 * OPTIMIZED: Single pass instead of two, plus sampling for large arrays.
 *
 * @param normalizedDensity - Normalized density values (0-1)
 * @param totalCells - Total number of cells
 * @returns Normalized entropy in [0, 1]
 */
export function calculateEntropy(normalizedDensity: Float32Array, totalCells: number): number {
  // Use sampling for large arrays
  const SAMPLE_THRESHOLD = 100000;
  const SAMPLE_SIZE = 20000;

  let samples: Float32Array;
  let sampleCount: number;

  if (totalCells > SAMPLE_THRESHOLD) {
    samples = new Float32Array(SAMPLE_SIZE);
    const step = totalCells / SAMPLE_SIZE;
    for (let i = 0; i < SAMPLE_SIZE; i++) {
      samples[i] = normalizedDensity[Math.floor(i * step)];
    }
    sampleCount = SAMPLE_SIZE;
  } else {
    samples = normalizedDensity;
    sampleCount = totalCells;
  }

  // Single pass: calculate sum and collect non-zero values
  let sumDensity = 0;
  for (let i = 0; i < sampleCount; i++) {
    sumDensity += samples[i];
  }

  if (sumDensity === 0) return 0;

  // Calculate Shannon entropy: H = -sum(p * log2(p))
  const invSum = 1 / sumDensity;
  let entropy = 0;
  for (let i = 0; i < sampleCount; i++) {
    const d = samples[i];
    if (d > 0) {
      const p = d * invSum;
      entropy -= p * Math.log2(p);
    }
  }

  // Normalize by maximum possible entropy (log2(n))
  const maxEntropy = Math.log2(sampleCount);
  return maxEntropy > 0 ? entropy / maxEntropy : 0;
}

/**
 * Calculate Gini coefficient for attention concentration
 *
 * Gini measures inequality of attention distribution:
 * - 0 = Perfectly equal distribution (same attention everywhere)
 * - 1 = Maximum inequality (all attention in one cell)
 *
 * OPTIMIZED: Uses stratified sampling for large datasets to avoid
 * sorting 2M+ elements. Sampling 10k elements provides <1% error
 * while being ~200x faster.
 *
 * @param normalizedDensity - Normalized density values (0-1)
 * @param totalCells - Total number of cells
 * @returns Gini coefficient in [0, 1]
 */
export function calculateGini(normalizedDensity: Float32Array, totalCells: number): number {
  // Use sampling for large arrays (threshold: 50k elements)
  const SAMPLE_THRESHOLD = 50000;
  const SAMPLE_SIZE = 10000;

  let samples: Float32Array;
  let sampleCount: number;

  if (totalCells > SAMPLE_THRESHOLD) {
    // Stratified sampling: take evenly spaced samples across the array
    samples = new Float32Array(SAMPLE_SIZE);
    const step = totalCells / SAMPLE_SIZE;
    for (let i = 0; i < SAMPLE_SIZE; i++) {
      samples[i] = normalizedDensity[Math.floor(i * step)];
    }
    sampleCount = SAMPLE_SIZE;
  } else {
    // Small array: use all elements
    samples = normalizedDensity;
    sampleCount = totalCells;
  }

  // Sort sampled values (10k sort is ~200x faster than 2M sort)
  const sorted = Array.from(samples).sort((a, b) => a - b);

  let sumDensity = 0;
  for (let i = 0; i < sampleCount; i++) {
    sumDensity += sorted[i];
  }

  if (sumDensity === 0) return 0;

  // Calculate Gini using the formula: G = (2 * sum(i * x_i)) / (n * sum(x_i)) - (n + 1) / n
  let weightedSum = 0;
  for (let i = 0; i < sampleCount; i++) {
    weightedSum += (i + 1) * sorted[i];
  }

  const gini = (2 * weightedSum) / (sampleCount * sumDensity) - (sampleCount + 1) / sampleCount;

  return Math.max(0, Math.min(1, gini));
}

/**
 * Detect hot spots using non-maximum suppression
 *
 * OPTIMIZED: Uses downsampling for initial scan on large grids.
 * For 2048x1024 grid, first scans at 256x128 (32x fewer pixels),
 * then refines candidates at full resolution.
 *
 * @param normalizedDensity - Normalized density grid
 * @param width - Grid width
 * @param height - Grid height
 * @param maxHotSpots - Maximum number of hot spots to return
 * @param minDistance - Minimum distance between hot spots in UV space
 * @param minDensity - Minimum normalized density for a hot spot
 * @param totalDensity - Total sum of density values
 * @returns Array of detected hot spots
 */
export function detectHotSpots(
  normalizedDensity: Float32Array,
  width: number,
  height: number,
  maxHotSpots: number,
  minDistance: number,
  minDensity: number,
  totalDensity: number,
): HotSpot[] {
  const hotSpots: HotSpot[] = [];

  // OPTIMIZED: Use downsampling for large grids
  const DOWNSAMPLE_THRESHOLD = 500000; // ~700x700
  const useDownsampling = width * height > DOWNSAMPLE_THRESHOLD;

  // Downsample factor (8 = check every 8th pixel in each dimension)
  const dsf = useDownsampling ? 8 : 1;
  const dsWidth = Math.ceil(width / dsf);
  const dsHeight = Math.ceil(height / dsf);

  // Find candidate regions at downsampled resolution
  const candidates: { x: number; y: number; density: number }[] = [];

  for (let dsy = 0; dsy < dsHeight; dsy++) {
    const y = Math.min(dsy * dsf, height - 1);
    for (let dsx = 0; dsx < dsWidth; dsx++) {
      const x = Math.min(dsx * dsf, width - 1);
      const idx = y * width + x;
      const d = normalizedDensity[idx];

      if (d < minDensity) continue;

      // For downsampled: just collect candidates above threshold
      // For full resolution: check local maximum
      if (useDownsampling) {
        candidates.push({ x, y, density: d });
      } else {
        // Check if local maximum (8-connected neighborhood)
        let isLocalMax = true;
        for (let dy = -1; dy <= 1 && isLocalMax; dy++) {
          for (let dx = -1; dx <= 1 && isLocalMax; dx++) {
            if (dx === 0 && dy === 0) continue;

            const ny = y + dy;
            let nx = x + dx;
            if (nx < 0) nx += width;
            else if (nx >= width) nx -= width;

            if (ny < 0 || ny >= height) continue;

            if (normalizedDensity[ny * width + nx] > d) {
              isLocalMax = false;
            }
          }
        }

        if (isLocalMax) {
          candidates.push({ x, y, density: d });
        }
      }
    }
  }

  // Sort by density descending
  candidates.sort((a, b) => b.density - a.density);

  // For downsampled candidates, refine to find true local maximum
  const refinedCandidates: { index: number; density: number; u: number; v: number }[] = [];

  for (const candidate of candidates) {
    // Limit candidates to process (top N by density)
    if (refinedCandidates.length >= maxHotSpots * 10) break;

    let bestX = candidate.x;
    let bestY = candidate.y;
    let bestDensity = candidate.density;

    if (useDownsampling) {
      // Search local area at full resolution to find true peak
      const searchRadius = dsf;
      for (let dy = -searchRadius; dy <= searchRadius; dy++) {
        const ny = candidate.y + dy;
        if (ny < 0 || ny >= height) continue;

        for (let dx = -searchRadius; dx <= searchRadius; dx++) {
          let nx = candidate.x + dx;
          if (nx < 0) nx += width;
          else if (nx >= width) nx -= width;

          const d = normalizedDensity[ny * width + nx];
          if (d > bestDensity) {
            bestDensity = d;
            bestX = nx;
            bestY = ny;
          }
        }
      }

      // Verify it's a local maximum
      let isLocalMax = true;
      for (let dy = -1; dy <= 1 && isLocalMax; dy++) {
        for (let dx = -1; dx <= 1 && isLocalMax; dx++) {
          if (dx === 0 && dy === 0) continue;

          const ny = bestY + dy;
          let nx = bestX + dx;
          if (nx < 0) nx += width;
          else if (nx >= width) nx -= width;

          if (ny < 0 || ny >= height) continue;

          if (normalizedDensity[ny * width + nx] > bestDensity) {
            isLocalMax = false;
          }
        }
      }

      if (!isLocalMax) continue;
    }

    refinedCandidates.push({
      index: bestY * width + bestX,
      density: bestDensity,
      u: bestX / width,
      v: 1 - bestY / height,
    });
  }

  // Re-sort refined candidates
  refinedCandidates.sort((a, b) => b.density - a.density);

  // Non-maximum suppression
  let hotSpotId = 0;
  for (const candidate of refinedCandidates) {
    if (hotSpots.length >= maxHotSpots) break;

    // Check distance to existing hot spots (with horizontal wrap-around)
    let tooClose = false;
    for (const existing of hotSpots) {
      const du = horizontalWrapDistance(candidate.u, existing.center.u);
      const dv = Math.abs(candidate.v - existing.center.v);
      const distance = Math.sqrt(du * du + dv * dv);
      if (distance < minDistance) {
        tooClose = true;
        break;
      }
    }

    if (!tooClose) {
      // Estimate radius (radius where density falls to 50% of peak)
      const radius = estimateHotSpotRadius(
        normalizedDensity,
        width,
        height,
        candidate.index,
        candidate.density,
      );

      // Calculate attention percentage in this hot spot
      const attentionPercentage = calculateHotSpotAttention(
        normalizedDensity,
        width,
        height,
        candidate.index,
        radius,
        totalDensity,
      );

      hotSpots.push({
        id: hotSpotId++,
        center: { u: candidate.u, v: candidate.v },
        peakDensity: candidate.density,
        radius,
        attentionPercentage,
      });
    }
  }

  return hotSpots;
}

/**
 * Calculate horizontal wrap distance between two U coordinates
 */
function horizontalWrapDistance(u1: number, u2: number): number {
  const directDistance = Math.abs(u1 - u2);
  const wrapDistance = 1 - directDistance;
  return Math.min(directDistance, wrapDistance);
}

/**
 * Estimate the radius of a hot spot (where density falls to 50% of peak)
 */
function estimateHotSpotRadius(
  density: Float32Array,
  width: number,
  height: number,
  centerIdx: number,
  peakDensity: number,
): number {
  const centerX = centerIdx % width;
  const centerY = Math.floor(centerIdx / width);
  const threshold = peakDensity * 0.5;

  // Find radius in 4 directions and average
  let totalRadius = 0;
  let validDirections = 0;

  const directions = [
    [1, 0], // right
    [-1, 0], // left
    [0, 1], // down
    [0, -1], // up
  ];

  for (const [dx, dy] of directions) {
    for (let r = 1; r < Math.max(width, height) / 4; r++) {
      let x = centerX + r * dx;
      const y = centerY + r * dy;

      // Handle horizontal wrap-around
      if (x < 0) x += width;
      if (x >= width) x -= width;

      // Stop at vertical boundaries
      if (y < 0 || y >= height) {
        totalRadius += r / Math.max(width, height);
        validDirections++;
        break;
      }

      const idx = y * width + x;
      if (density[idx] < threshold) {
        // Convert radius to UV space (approximate)
        totalRadius += r / Math.max(width, height);
        validDirections++;
        break;
      }
    }
  }

  return validDirections > 0 ? totalRadius / validDirections : 0.05;
}

/**
 * Calculate the percentage of total attention within a hot spot
 */
function calculateHotSpotAttention(
  density: Float32Array,
  width: number,
  height: number,
  centerIdx: number,
  radius: number,
  totalDensity: number,
): number {
  if (totalDensity === 0) return 0;

  const centerX = centerIdx % width;
  const centerY = Math.floor(centerIdx / width);
  const radiusPixels = Math.ceil(radius * Math.max(width, height));

  let hotSpotDensity = 0;

  for (let dy = -radiusPixels; dy <= radiusPixels; dy++) {
    const y = centerY + dy;
    if (y < 0 || y >= height) continue;

    for (let dx = -radiusPixels; dx <= radiusPixels; dx++) {
      let x = centerX + dx;

      // Handle horizontal wrap-around
      if (x < 0) x += width;
      if (x >= width) x -= width;

      // Check if within radius (circular)
      const distanceU = dx / width;
      const distanceV = dy / height;
      const distance = Math.sqrt(distanceU * distanceU + distanceV * distanceV);

      if (distance <= radius) {
        hotSpotDensity += density[y * width + x];
      }
    }
  }

  return (hotSpotDensity / totalDensity) * 100;
}

/**
 * Extract density grid from ImageData
 * Used when we have a rendered heatmap but need raw density values
 *
 * @param imageData - Heatmap ImageData
 * @returns Float32Array of density values (based on alpha channel)
 */
export function extractDensityFromImageData(imageData: ImageData): Float32Array {
  const { data, width, height } = imageData;
  const density = new Float32Array(width * height);

  for (let i = 0; i < density.length; i++) {
    // Use alpha channel as density (normalized to 0-1)
    // The heatmap generation sets alpha based on density
    density[i] = data[i * 4 + 3] / 255;
  }

  return density;
}

/**
 * Calculate statistics directly from heatmap ImageData
 *
 * @param imageData - Heatmap ImageData
 * @param config - Configuration options
 * @returns Coverage statistics
 */
export function calculateStatisticsFromImageData(
  imageData: ImageData,
  config: Partial<CoverageStatisticsConfig> = {},
): CoverageStatistics {
  const density = extractDensityFromImageData(imageData);
  return calculateCoverageStatistics(density, imageData.width, imageData.height, config);
}
